import axios from "axios";
import format from "date-fns/format";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useCallback } from "react";
import { useSnackbar } from "notistack";
import { useAxiosHook } from "./useAxiosHook";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import cleanEmpty from "../_src_shared/utils/objects/cleanEmpty";
import CustomSnackbar from "../_src_shared/components/CustomSnackbar";
import {
  OCROLUS_PDF_ONLY_MESSAGE,
  ocrolusDocumentLabel,
  partitionOcrolusDocuments,
} from "../utils/documents/ocrolusFileValidation";
import { isArray, isFunction } from "lodash";

const ROUTE_PREFIX = "files";

// TO DO
// make general file upload endpoints
// -- uploadSingleFile({file, onSuccessFn, onFailFn})
// -- uploadMultipleFiles({files, onSuccessFn, onFailFn})
// -- uploadFilesWithFormData({formData, onSuccessFn, onFailFn})

export const useLoanFilesHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Ocrolus rejects anything that isn't a PDF — and it does so INSIDE a 200
  // envelope (`{'status': 200, 'response': {'status': 400, 'message': '… is
  // not a valid pdf'}}`), which the backend turns into a 500 ValueError
  // ("upload response missing uploaded_docs"). So a PNG/JPEG in the batch
  // doesn't fail gracefully; it fails loud, and everything after it in the
  // batch never gets sent. Gate here, in front of BOTH send functions, so no
  // caller can reach the backend with one. Returns the sendable docs, or null
  // when the batch has to stop.
  const guardOcrolusBatch = (docs, onFailFn) => {
    const { supported, unsupported } = partitionOcrolusDocuments(docs);
    if (!unsupported.length) return supported;
    const names = unsupported.map(ocrolusDocumentLabel);
    const shown = names.slice(0, 3).join(", ");
    const more = names.length > 3 ? ` and ${names.length - 3} more` : "";
    enqueueSnackbar(
      `${OCROLUS_PDF_ONLY_MESSAGE} Not sent: ${shown}${more}.`,
      {
        autoHideDuration: 7000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        content: (key, message) => (
          <CustomSnackbar
            id={key}
            message={message}
            variant="warning"
            closeSnackbar={() => closeSnackbar(key)}
          />
        ),
      },
    );
    if (isFunction(onFailFn)) onFailFn(new Error("ocrolus_non_pdf"));
    return null;
  };

  // -------------------------------------
  //  POST /files/upload_pipeline_files/{container_name}
  // -------------------------------------
  const uploadPipelineFiles = async ({
    containerName,
    data,
    onSuccessFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/upload_pipeline_files/${containerName}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Upload Pipeline Files",
      path: endpoint,
      payload: data, // data is your FormData with submission + files
      cancelSource: source,
      onSuccessFn,
      onFailFn,
      // Ensure your useAxiosHook sets up correct headers for multipart/form-data
    };
    await axiosPostRequest(params);
  };

  // -------------------------------------
  //  POST /files/upload_loan_docs/{loanId}
  // -------------------------------------
  const uploadLoanDocs = async ({
    loanId,
    files,
    onSuccessFn,
    onSuccessMsg = "Files Uploaded",
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(files)) {
      console.error("Missing loanId");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/upload_loan_docs/${loanId}`;
    const source = axios.CancelToken.source();
    /**
     const params = {
      name: "Create Loan Application",
      path: endpoint,
      payload,
      cancelSource: source,
      formData: true,
      includeQueryParams: true,
      errorTesting,
      onSuccessFn,
      };
      
      */

    await axiosPostRequest({
      name: "Upload Loan Docs",
      path: endpoint,
      files, // FormData with submission + files
      formData: true,
      // errorTesting: true,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /files/upload_loan_sizer/{loanId}
  // -------------------------------------
  const uploadLoanSizer = async ({
    loanId,
    payload,
    onSuccessFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(payload)) {
      console.error("Missing loanId or payload");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/upload_loan_sizer/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Upload Loan Sizer",
      path: endpoint,
      formData: true,
      payload, // FormData with submission + files
      cancelSource: source,
      onSuccessFn,
      onFailFn,
      onSuccessMsg: "Sizer Uploaded",
      //

      // includeQueryParams: true,
      // onSuccessFn,
      // hideFailMsg: true,
      // onCompleteFn,
    });
  };

  // -------------------------------------
  //  POST /files/classify_loan_sizer/{dealId}
  //  Promote an existing "Terms & Sizer" loan_documents element into the sizer
  //  tracker (sizer_uploads.latest_versions) by tagging it with a Type + Stage —
  //  no re-upload. Counterpart to upload_loan_sizer. Returns the tracker row.
  //  payload: { sizerDocumentId, sizerType, sizerStage, extractSizerData? }
  // -------------------------------------
  const classifyLoanSizer = async ({
    dealId,
    payload,
    onSuccessFn,
    onFailFn,
  }) => {
    if (isNil(dealId) || isNil(payload)) {
      console.error("Missing dealId or payload");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/classify_loan_sizer/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Classify Loan Sizer",
      path: endpoint,
      payload, // { sizerDocumentId, sizerType, sizerStage, extractSizerData }
      cancelSource: source,
      onSuccessFn,
      onFailFn,
      onSuccessMsg: "Sizer classified",
    });
  };

  // -------------------------------------
  //  POST /files/update_loan_doc_metadata/{loanId}
  // -------------------------------------
  const updateLoanDocMetadata = async ({
    loanId,
    data,
    onSuccessFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/update_loan_doc_metadata/${loanId}`;
    const source = axios.CancelToken.source();

    // Reflect how many documents were actually updated so a bulk edit reads as
    // a bulk edit (previously a no-op ternary that always said "Loan Files
    // Updated", which made a multi-doc update look like it only touched one).
    const updatedCount = size(data);
    const successMsg = `${updatedCount} ${
      updatedCount === 1 ? "file" : "files"
    } updated`;
    await axiosPostRequest({
      name: "Update Loan Doc Metadata",
      path: endpoint,
      payload: data, // typically an array of JSON objects
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: successMsg,
      onFailFn,
      // errorTesting: true,
    });
  };

  // -------------------------------------
  //  GET /files/download_loan_docs/{loanId}
  // -------------------------------------
  const downloadLoanDocs = async ({
    loanId,
    fileDownloadName,
    onSuccessFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/download_loan_docs/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Download Loan Docs",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
      isFile: true,
      fileDownload: true,
      fileDownloadName,
      // You may want to configure axios to handle binary data
      // (e.g. responseType: "blob") if you're actually downloading files
    });
  };


  // -------------------------------------
  //  GET /files/document_package/{loanId}/{selector}
  //
  //  Builds the zip of every document carrying the selector's share flag
  //  (see utils/documents/documentShareFlags.js) and streams it back as an
  //  attachment, foldered by docGroup. Selectors: `third_party_review` |
  //  `counterparty` | `tpr_approved`.
  //
  //  Side effects on the backend: the zip is ALSO staged as a tracked loan
  //  document (docGroup "Document Packages") and every included doc gets a
  //  `shareStamp`. Callers should refetch the loan afterwards so the staged
  //  package + stamps show up. 400s with a clear message when nothing
  //  carries the flag.
  // -------------------------------------
  const downloadDocumentPackage = useCallback(
    async ({ loanId, selector, fileDownloadName, onSuccessFn, onFailFn }) => {
      if (isNil(loanId) || isNil(selector)) {
        console.error("Missing loanId or selector");
        return;
      }
      const endpoint = `${ROUTE_PREFIX}/document_package/${loanId}/${selector}`;
      const source = axios.CancelToken.source();

      await axiosGetRequest({
        name: "Download Document Package",
        path: endpoint,
        cancelSource: source,
        isFile: true,
        fileDownload: true,
        fileDownloadName,
        onSuccessFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  // -------------------------------------
  //  POST /files/push_document_package/{loanId}
  //
  //  Same build as document_package but WITHOUT the download — the zip is
  //  staged as a loan document and the response hands back the blob url:
  //  { packageOid, fileName, blob_url, includedCount, includedDocumentOids,
  //    target, note }. External SFTP/API delivery is a backend follow-up;
  //  the staged blob is the handoff today.
  // -------------------------------------
  const pushDocumentPackage = useCallback(
    async ({ loanId, selector, target, onSuccessFn, onFailFn }) => {
      if (isNil(loanId) || isNil(selector)) {
        console.error("Missing loanId or selector");
        return;
      }
      const endpoint = `${ROUTE_PREFIX}/push_document_package/${loanId}`;
      const source = axios.CancelToken.source();

      await axiosPostRequest({
        name: "Push Document Package",
        path: endpoint,
        payload: cleanEmpty({ selector, target }),
        cancelSource: source,
        onSuccessMsg: "Package staged to the loan",
        onSuccessFn,
        onFailFn,
      });
    },
    [axiosPostRequest],
  );

  // -------------------------------------
  //  GET /files/file_by_id/{loanId}/{file_storage_name}
  // -------------------------------------
  const getFileById = useCallback(
    async ({
      loanId,
      file_storage_name,
      isFile = true,
      fileDownloadName,
      onSuccessFn,
      fileDownload,
      openInNewTab,
      onFailFn,
    }) => {
      console.log({ loanId, file_storage_name });
      if (!loanId || !file_storage_name) {
        console.error("Missing loanId or file_storage_name");
        // setError("Missing loanId or file_storage_name");
        // setLoading(false);
        return;
      }
      const endpoint = `${ROUTE_PREFIX}/file_by_id/${loanId}/${file_storage_name}`;
      const source = axios.CancelToken.source();
      const params = {
        name: "Get File By Id",
        path: endpoint,
        cancelSource: source,
        isFile,
        fileDownloadName,
        fileDownload,
        openInNewTab,
        onSuccessFn,
        onFailFn,
      };
      await axiosGetRequest(params);
    },
    [axiosGetRequest],
  );

  // -------------------------------------
  //  GET /files/file_web_view_url/{loanId}/{file_storage_name}
  //  Returns a short-lived, anonymously-fetchable URL (Azure SAS) for the blob
  //  so it can be handed to the Microsoft Office Online viewer
  //  (https://view.officeapps.live.com/op/view.aspx?src=<url>) for in-browser
  //  viewing WITHOUT any login. Response shape: { url }.
  //  NOTE: backend endpoint pending — see the office-web-view spec.
  // -------------------------------------
  const getFileWebViewUrl = useCallback(
    async ({ loanId, file_storage_name, onSuccessFn, onFailFn }) => {
      if (!loanId || !file_storage_name) {
        console.error("Missing loanId or file_storage_name");
        return;
      }
      const endpoint = `${ROUTE_PREFIX}/file_web_view_url/${loanId}/${file_storage_name}`;
      const source = axios.CancelToken.source();
      await axiosGetRequest({
        name: "Get File Web View Url",
        path: endpoint,
        cancelSource: source,
        onSuccessFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  // -------------------------------------
  //  GET /files/download_pipeline/{pipeline_criteria}
  // -------------------------------------
  const downloadPipeline = async ({
    pipelineCriteria = "all_deals", // 'all_deals' || 'underwriting' || 'closed'
    onSuccessFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/download_pipeline/${pipelineCriteria}`;
    const source = axios.CancelToken.source();
    const shorthandDate = format(new Date(), "M-d-yy");
    const file_name = `Loan Pipeline - ${shorthandDate}`;
    const params = {
      name: "Download Pipeline",
      path: endpoint,
      cancelSource: source,
      isFile: true,
      fileDownload: true,
      fileDownloadName: file_name,
      onSuccessFn,
      onFailFn,
      // Possibly set { responseType: "blob" } if you're downloading an Excel
    };
    await axiosGetRequest(params);
  };

  const downloadStageDurationReport = async ({ onSuccessFn, onFailFn }) => {
    const endpoint = `${ROUTE_PREFIX}/download_stage_duration_report`;
    const source = axios.CancelToken.source();
    const shorthandDate = format(new Date(), "M-d-yy");
    const file_name = `Stage Duration Report - ${shorthandDate}`;

    await axiosGetRequest({
      name: "Download Stage Duration Report",
      path: endpoint,
      cancelSource: source,
      isFile: true,
      fileDownload: true,
      fileDownloadName: file_name,
      onSuccessFn,
      onFailFn,
      // Possibly set { responseType: "blob" } if you're downloading an Excel
    });
  };

  // -------------------------------------
  //  GET /files/wire_worksheet/{dealId}
  // -------------------------------------
  // The net-wire build-down as a live-formula .xlsx — the same math the Net
  // Wire Calculator renders, in the workbook closers actually hand to title.
  // Surfaced from the NWC card on the Wire & Funding tab.
  const downloadWireWorksheet = async ({
    dealId,
    syncLoanId,
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/wire_worksheet/${dealId}`;
    const source = axios.CancelToken.source();
    const file_name = `Wire Worksheet - ${syncLoanId || dealId}`;

    await axiosGetRequest({
      name: "Download Wire Worksheet",
      path: endpoint,
      cancelSource: source,
      isFile: true,
      fileDownload: true,
      fileDownloadName: file_name,
      onSuccessFn,
      onFailFn,
    });
  };

  const downloadLoanUploadTemplate = async ({ onSuccessFn, onFailFn }) => {
    const endpoint = `${ROUTE_PREFIX}/get_deal_upload_template`;
    const source = axios.CancelToken.source();

    const file_name = `Quick Intake Loan Template`;

    await axiosGetRequest({
      name: "Download Loan Upload Template",
      path: endpoint,
      cancelSource: source,
      isFile: true,
      fileDownload: true,
      fileDownloadName: file_name,
      onSuccessFn,
      onFailFn,
      // Possibly set { responseType: "blob" } if you're downloading an Excel
    });
  };

  const getAllSizerUploads = async ({ onSuccessFn, onFailFn }) => {
    const endpoint = `${ROUTE_PREFIX}/get_all_sizer_uploads`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get All Sizer Uploads",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
      onFailMsg: "Failed to fetch all loan sizer uploads",
    });
  };
  const getAllSizerUploadsByDealId = async ({
    onSuccessFn,
    dealId,
    onFailFn,
  }) => {
    if (isNil(dealId)) {
      console.error("Missing dealId");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/get_deal_sizer_uploads/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get All Sizer Uploads By Deal ID",
      path: endpoint,
      cancelSource: source,
      onFailMsg: "Failed to fetch sizer uploads for this deal",

      onSuccessFn: (d) => {
        if (isFunction(onSuccessFn)) {
          const data = isArray(d) ? d : [];
          if (data[0]?._id) {
            console.log("real", data);
            onSuccessFn(data);
          } else {
            console.log("empty", data);
            // console.warn("Received data but missing expected _id field:", data);
            onSuccessFn([]);
          }
        }
      },
      onFailFn,
    });
  };

  const getLoanDocAuditLogs = async ({ onSuccessFn, dealId, onFailFn }) => {
    if (isNil(dealId)) {
      console.error("Missing dealId");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/loan_doc_audit_logs/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Loan Document Audit Logs",
      path: endpoint,
      cancelSource: source,
      onFailMsg: "Failed to fetch loan document audit logs for this deal",

      onSuccessFn,
      onFailFn,
    });
  };

  const associateDocumentWithObjects = async ({
    onSuccessFn,
    dealId,
    onFailFn,
    loanDocumentsIds,
    payload,
    borrowerIds,
    subjectPropertyIds,
    entityIds,
  }) => {
    if (isNil(dealId)) {
      console.error("Missing dealId");
      return;
    }
    validateRequiredFields({
      dealId,
    });
    const payloadWithIds = {
      loanDocuments: loanDocumentsIds,
      borrowers: borrowerIds,
      subjectProperties: subjectPropertyIds,
      entities: entityIds,
      ...payload,
    };

    const cleanedPayload = cleanEmpty(payloadWithIds);
    // const cleanedPayload = removeEmptyArrays(payloadWithIds);

    // console.log(";cleanedPayload", cleanedPayload);
    // return;
    const endpoint = `${ROUTE_PREFIX}/associate_document_with_objects/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Associate Document With Objects",
      path: endpoint,
      cancelSource: source,
      onSuccessMsg: "Successfully associated document(s)",
      payload: cleanedPayload,

      onSuccessFn,
      onFailFn,
    });
  };

  const sendBankStatementsToOcrulus = async ({
    onSuccessFn,
    dealId,
    files,
    onFailFn,
  }) => {
    if (isNil(dealId)) {
      console.error("Missing dealId");
      return;
    }
    validateRequiredFields({
      dealId,
      files,
    });

    // PDF-only gate — the legacy Files tab that calls this has no selection
    // guard of its own, so this is the only thing between it and the backend.
    const sendable = guardOcrolusBatch(files, onFailFn);
    if (!sendable) return;

    // The legacy misspelled path (`..._ocrulus`) is only an alias decorator on
    // the same handler and gets removed once both portals migrate, so this
    // calls the correctly-spelled route. The function name keeps the old
    // spelling for its (legacy Files tab) caller.
    const endpoint = `${ROUTE_PREFIX}/bank_statements_to_ocrolus/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Send Bank Statements To Ocrulus",
      path: endpoint,
      cancelSource: source,
      onSuccessMsg: "Successfully sent bank statements to Ocrulus",
      payload: sendable,
      onSuccessFn,
      onFailFn,
    });
  };

  const parseAndExtractDocuments = async ({
    onSuccessFn,
    dealId,
    files,
    onFailFn,
  }) => {
    if (isNil(dealId)) {
      console.error("Missing dealId");
      return;
    }
    validateRequiredFields({
      dealId,
      files,
    });

    const endpoint = `${ROUTE_PREFIX}/parse_extract_docs/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Parse And Extract Documents",
      path: endpoint,
      cancelSource: source,
      onSuccessMsg: "Successfully parsed and extracted documents",
      payload: files,
      onSuccessFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /files/bank_statements_to_ocrolus/{dealId}
  //
  //  Sends the selected bank-statement documents to Ocrolus for processing.
  //  Body is the raw array of loan-document objects (the backend reads
  //  request_body as `documents_sent`). Auto-creates/uses the loan's Ocrolus
  //  book. See eastview-os frontend_documentation/ocrolus-book-and-notes.md.
  // -------------------------------------
  const sendBankStatementsToOcrolus = async ({
    dealId,
    documents,
    onSuccessFn,
    onFailFn,
  }) => {
    if (isNil(dealId)) {
      console.error("Missing dealId");
      return;
    }
    // PDF-only gate. The Ocrolus tab and the Uploaded bulk bar already stop a
    // non-PDF from being selected; this is the backstop for any caller.
    const sendable = guardOcrolusBatch(documents, onFailFn);
    if (!sendable) return;

    const endpoint = `${ROUTE_PREFIX}/bank_statements_to_ocrolus/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Send Bank Statements To Ocrolus",
      path: endpoint,
      cancelSource: source,
      onSuccessMsg: "Bank statements sent to Ocrolus for processing.",
      payload: sendable,
      onSuccessFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /files/create_ocrolus_book/{dealId}
  //
  //  "Create Book from Loan". No body. IDEMPOTENT — one book per loan,
  //  anchored on Ocrolus `xid = dealId`; calling again returns the existing
  //  book rather than creating a second one. Book name is the loan's
  //  sync_loan_id when present, else "LQ Portal {loanNumber}".
  //  -> { dealId, book_uuid, book_name, filesSent, message }
  // -------------------------------------
  const createOcrolusBook = async ({ dealId, onSuccessFn, onFailFn }) => {
    if (isNil(dealId)) {
      console.error("Missing dealId");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_ocrolus_book/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Ocrolus Book",
      path: endpoint,
      cancelSource: source,
      onSuccessMsg: "Ocrolus book ready.",
      payload: {},
      onSuccessFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  GET /files/ocrolus_book/{dealId}
  //
  //  Book status for the UI: book identity, filesSent, which analytics have
  //  landed (accountsReady / cashFlowReady), fraud `signals`
  //  ({ riskLevel, total, analyzedAt }), the compact bankStatementSummary, and
  //  when the loan's dedicated ocrolus NOTE was last rewritten.
  //  `book_uuid == null` means no book exists yet.
  //
  //  Results arrive by webhook, so filesSent > 0 with accountsReady false is
  //  the normal "processing" state, not an error.
  // -------------------------------------
  const getOcrolusBook = async ({ dealId, onSuccessFn, onFailFn }) => {
    if (isNil(dealId)) {
      console.error("Missing dealId");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/ocrolus_book/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Ocrolus Book",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  GET /files/ocrolus_books?scope=all|portal
  //
  //  Transition-period book browser. The Ocrolus account is SHARED between
  //  portals and plenty of books were created outside the LOS under ad-hoc
  //  names — this lists them annotated with the loan each maps to, so ops can
  //  find an orphan and assign it.
  //
  //  scope='all' (default) is unfiltered; every row carries a `portalHint`
  //  ('lq' | 'ev' | 'unknown') for client-side filtering. scope='portal'
  //  applies the leakage guard and reports what it removed as `hiddenCount`.
  // -------------------------------------
  const getOcrolusBooks = async ({ scope = "all", onSuccessFn, onFailFn }) => {
    const qs = scope ? `?scope=${encodeURIComponent(scope)}` : "";
    const endpoint = `${ROUTE_PREFIX}/ocrolus_books${qs}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "List Ocrolus Books",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /files/assign_ocrolus_book/{dealId}
  //
  //  Attach an EXISTING book to this loan (the transition path for books
  //  created outside the LOS). Body: { book_uuid, rename? (default true),
  //  force? }. Stores the mapping + provenance, RENAMES the book to the loan's
  //  EV#/LQ# and re-anchors xid = dealId (best-effort — the LOS-side mapping
  //  stands even if the Ocrolus update fails), then pulls book results + fraud
  //  signals inline so the summary and the ocrolus note land immediately.
  //
  //  A book backs exactly ONE loan: reassigning one that already belongs to a
  //  different deal returns 409 unless `force`. That status is suppressed here
  //  so the caller can put up a confirm and retry with force instead of the
  //  generic error toast.
  // -------------------------------------
  const assignOcrolusBook = async ({
    dealId,
    bookUuid,
    rename = true,
    force = false,
    onSuccessFn,
    onFailFn,
  }) => {
    if (isNil(dealId) || isNil(bookUuid)) {
      console.error("Missing dealId or bookUuid");
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/assign_ocrolus_book/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Assign Ocrolus Book",
      path: endpoint,
      cancelSource: source,
      onSuccessMsg: "Book assigned to this loan.",
      payload: { book_uuid: bookUuid, rename, force },
      suppressFailStatuses: [409],
      onSuccessFn,
      onFailFn,
    });
  };

  const getParseAndExtractDocuments = async ({
    onSuccessFn,
    dealId,

    onFailFn,
  }) => {
    if (isNil(dealId)) {
      console.error("Missing dealId");
      return;
    }
    validateRequiredFields({
      dealId,
    });

    const endpoint = `${ROUTE_PREFIX}/get_parsed_documents/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Parse And Extract Documents",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /files/upload_loan_docs_with_associations/{loanId}
  //
  //  Single atomic call: uploads the files AND attaches them to the
  //  given borrowers / subject properties / entities. Backend returns
  //  the refreshed deal plus `_meta.uploadedLoanDocumentIds` so the
  //  caller doesn't have to diff loanDocuments.
  //
  //  When all three id arrays are empty/omitted, the backend behaves
  //  exactly like /upload_loan_docs.
  // -------------------------------------
  const uploadLoanDocsWithAssociations = useCallback(
    async ({
      loanId,
      files,
      borrowerIds,
      subjectPropertyIds,
      entityIds,
      onSuccessFn,
      onSuccessMsg = "File(s) Uploaded",
      onFailFn,
    }) => {
      if (isNil(loanId) || isNil(files)) {
        console.error("Missing loanId or files");
        return;
      }
      const endpoint = `${ROUTE_PREFIX}/upload_loan_docs_with_associations/${loanId}`;
      const source = axios.CancelToken.source();

      const associations = cleanEmpty({
        borrowers: borrowerIds,
        subjectProperties: subjectPropertyIds,
        entities: entityIds,
      });

      await axiosPostRequest({
        name: "Upload Loan Docs With Associations",
        path: endpoint,
        files,
        formData: true,
        payload: { associations },
        cancelSource: source,
        onSuccessMsg,
        onSuccessFn,
        onFailFn,
      });
    },
    [axiosPostRequest],
  );

  // -------------------------------------
  //  Legacy alias (singular). Delegates to uploadLoanDocsWithAssociations
  //  so existing callers keep working but go through the atomic backend
  //  endpoint. `onUploadSuccessFn` is preserved as a best-effort signal —
  //  it now fires alongside onSuccessFn since upload + associate happen
  //  in one round-trip.
  // -------------------------------------
  const uploadLoanDocsWithAssociation = useCallback(
    async ({
      loanId,
      files,
      borrowerIds,
      subjectPropertyIds,
      entityIds,
      onUploadSuccessFn,
      onSuccessFn,
      onSuccessMsg,
      onFailFn,
    }) => {
      await uploadLoanDocsWithAssociations({
        loanId,
        files,
        borrowerIds,
        subjectPropertyIds,
        entityIds,
        onSuccessMsg,
        onSuccessFn: (d) => {
          onUploadSuccessFn?.(d);
          onSuccessFn?.(d);
        },
        onFailFn,
      });
    },
    [uploadLoanDocsWithAssociations],
  );
  return {
    error,
    loading,

    // POST
    uploadPipelineFiles,
    uploadLoanDocs,
    uploadLoanSizer,
    classifyLoanSizer,
    updateLoanDocMetadata,
    getAllSizerUploadsByDealId,
    associateDocumentWithObjects,
    parseAndExtractDocuments,
    sendBankStatementsToOcrolus,
    sendBankStatementsToOcrulus,
    createOcrolusBook,
    assignOcrolusBook,
    uploadLoanDocsWithAssociation,
    uploadLoanDocsWithAssociations,
    pushDocumentPackage,
    // GET
    downloadLoanUploadTemplate,
    downloadLoanDocs,
    downloadDocumentPackage,
    getFileById,
    getFileWebViewUrl,
    downloadPipeline,
    getAllSizerUploads,
    getLoanDocAuditLogs,
    getParseAndExtractDocuments,
    getOcrolusBook,
    getOcrolusBooks,
    downloadStageDurationReport,
    downloadWireWorksheet,
  };
};
